import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import { API_BASE } from "../utils/api";
const API_URL = API_BASE;

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Invalid email or password. Please try again.");
        return;
      }
      login(data.access_token, data.user);
      navigate("/dashboard");
    } catch (err) {
      setError("Unable to connect to server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || !form.email || !form.password;

  return (
    <div className="min-h-screen flex bg-luxury-bg">

      {/* Left Panel: Dark Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden bg-luxury-surface border-r border-luxury-border">

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-11 h-11 bg-luxury-elevated border border-luxury-gold/40 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <span className="text-luxury-text font-bold text-xl">TaxVault</span>
            <p className="text-luxury-muted text-xs">South Africa</p>
          </div>
        </div>

        {/* Headline + features */}
        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-luxury-text leading-tight mb-3">
            Receipts to<br />
            Tax Packs<br />
            <span className="text-luxury-gold">Instantly.</span>
          </h2>
          <p className="text-luxury-secondary text-sm leading-relaxed mb-10">
            The smart receipt vault for South African taxpayers. Organise and access your tax documents anytime.
          </p>

          <div className="space-y-4">
            {[
              { title: "Bank-level Security", desc: "AES-256 encrypted document storage" },
              { title: "Auto-Organised", desc: "Tax returns, payslips & invoices, sorted" },
              { title: "SARS Compliant", desc: "Built for South African requirements" },
              { title: "Instant Access", desc: "Retrieve any document in seconds" },
            ].map((f) => (
              <div key={f.title} className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-luxury-gold mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-luxury-text text-sm font-semibold">{f.title}</p>
                  <p className="text-luxury-muted text-xs">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-luxury-muted text-xs">© 2026 TaxVault · Proudly South African</p>
        </div>
      </div>

      {/* Right Panel: Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-luxury-bg px-6 py-12">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 bg-luxury-elevated border border-luxury-gold/40 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className="text-luxury-text font-bold text-lg">TaxVault</span>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-luxury-text">Welcome back</h1>
            <p className="text-luxury-secondary mt-1.5 text-sm">Sign in to your TaxVault account</p>
          </div>

          {/* Error alert */}
          {error && (
            <div className="flex items-start gap-3 bg-red-900/20 border border-red-800/50 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-luxury-secondary mb-2">Email Address</label>
              <input
                type="email" name="email" value={form.email}
                onChange={handleChange} required autoComplete="email"
                placeholder="you@company.co.za"
                className="w-full px-4 py-3 border border-luxury-border rounded-xl focus:outline-none focus:ring-2 focus:ring-luxury-gold/40 focus:border-luxury-gold/60 text-luxury-text text-sm bg-luxury-surface placeholder-luxury-muted transition"
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-luxury-secondary">Password</label>
                <a href="/forgot-password" className="text-xs text-luxury-gold hover:text-luxury-gold-dim font-medium transition-colors">
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"} name="password"
                  value={form.password} onChange={handleChange}
                  required autoComplete="current-password" placeholder="••••••••"
                  className="w-full px-4 py-3 pr-12 border border-luxury-border rounded-xl focus:outline-none focus:ring-2 focus:ring-luxury-gold/40 focus:border-luxury-gold/60 text-luxury-text text-sm bg-luxury-surface placeholder-luxury-muted transition"
                />
                <button type="button" tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-luxury-muted hover:text-luxury-secondary transition p-1"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button type="submit" disabled={isDisabled}
              className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200
                ${isDisabled
                  ? "bg-luxury-elevated text-luxury-muted cursor-not-allowed border border-luxury-border"
                  : "bg-luxury-cta hover:bg-luxury-cta-hover text-white active:scale-95"
                }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in...
                </span>
              ) : "Sign In →"}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-luxury-border" />
            <span className="text-xs text-luxury-muted font-medium">New to TaxVault?</span>
            <div className="flex-1 h-px bg-luxury-border" />
          </div>

          {/* Register link */}
          <Link to="/register"
            className="block w-full text-center py-3 rounded-xl border border-luxury-border text-luxury-secondary font-semibold text-sm hover:border-luxury-gold/50 hover:text-luxury-gold transition-all duration-200"
          >
            Create a free account
          </Link>

          {/* Trust note */}
          <p className="text-center text-xs text-luxury-muted mt-6 flex items-center justify-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Secured with 256-bit encryption
          </p>
        </div>
      </div>

    </div>
  );
}
